/**
 * Canonical POI category registry (frontend mirror of `services/poi/registry.py`).
 *
 * The IDs below MUST match the backend `LAYERS` dict exactly. Labels/
 * translations/icons are frontend concerns; the IDs are the shared
 * language between the map, the layer switcher, the AINA router, and
 * every future POI provider.
 *
 * Adding a new POI category:
 *   1. Add it to `services/poi/registry.py`  (source of truth)
 *   2. Add it to this file with a lucide icon + translation key
 *   Do NOT rename IDs — deep-links and AINA intents rely on them.
 */
import {
  Utensils, Hotel, Fuel, Zap, Landmark, Bath, Building2,
  Shield, Flame, MapPin, Siren, AlertCircle, CircleDollarSign,
} from "lucide-react";

// Clustering constants — configurable in one place so per-category
// overrides remain trivial to add later without a map rewrite.
export const CLUSTER_RADIUS   = 50;   // px on the map
export const CLUSTER_MAX_ZOOM = 14;   // clusters break apart past this zoom

/**
 * Per-category display config. `id` mirrors the backend layer ID.
 * `tKey` maps into the account-level i18n table; `label` is the English
 * fallback. `Icon` is a lucide-react component (renders inside a DOM
 * marker element — see maplibre provider). `color` is the marker fill.
 */
export const POI_CATEGORIES = {
  restaurant: { id: "restaurant", tKey: "poi.restaurant.label", label: "Restaurants",   Icon: Utensils,          color: "#f97316" },
  hotel:      { id: "hotel",      tKey: "poi.hotel.label",      label: "Hotels",        Icon: Hotel,             color: "#0ea5e9" },
  fuel:       { id: "fuel",       tKey: "poi.fuel.label",       label: "Fuel",          Icon: Fuel,              color: "#eab308" },
  ev:         { id: "ev",         tKey: "poi.ev.label",         label: "EV Charging",   Icon: Zap,               color: "#10b981" },
  atm:        { id: "atm",        tKey: "poi.atm.label",        label: "ATM",           Icon: CircleDollarSign,  color: "#6366f1" },
  washroom:   { id: "washroom",   tKey: "poi.washroom.label",   label: "Washrooms",     Icon: Bath,              color: "#0891b2" },
  hospital:   { id: "hospital",   tKey: "poi.hospital.label",   label: "Hospitals",     Icon: AlertCircle,       color: "#dc2626" },
  police:     { id: "police",     tKey: "poi.police.label",     label: "Police",        Icon: Shield,            color: "#1e40af" },
  fire:       { id: "fire",       tKey: "poi.fire.label",       label: "Fire",          Icon: Flame,             color: "#b91c1c" },
  government: { id: "government", tKey: "poi.government.label", label: "Government",    Icon: Landmark,          color: "#374151" },
  tourist:    { id: "tourist",    tKey: "poi.tourist.label",    label: "Tourist / POI", Icon: MapPin,            color: "#a855f7" },
  emergency:  { id: "emergency",  tKey: "poi.emergency.label",  label: "Emergency",     Icon: Siren,             color: "#dc2626" },
};

// Per-marker action translation keys — used by the POI detail sheet so
// "Call" / "Directions" / "View" localise consistently across every
// module that opens a POI.
export const POI_ACTION_KEYS = {
  call:       "poi.action.call",
  directions: "poi.action.directions",
  view:       "poi.action.view",
  close:      "poi.action.close",
};

/** Return the display config for an ID (falls back to a neutral pin). */
export function getCategoryConfig(id) {
  return POI_CATEGORIES[id] || {
    id: id || "unknown", tKey: null, label: id || "Place",
    Icon: MapPin, color: "#0f766e",
  };
}
